import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Table from 'react-bootstrap/Table';
import { db, functions } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';
import NewChallengeModal from './NewChallengeModal.js';

function formatGameDate(g) {
  const ts = g.status === 'completed' ? g.finishedAt : g.createdAt;
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function statusLabel(g, currentUser) {
  if (g.status === 'completed') {
    const mySide = g.player1Uid === currentUser.uid ? 'player1' : 'player2';
    const opponentSide = mySide === 'player1' ? 'player2' : 'player1';
    const myScore = g.scores[mySide];
    const opponentScore = g.scores[opponentSide];
    const result = myScore > opponentScore ? 'Won' : myScore < opponentScore ? 'Lost' : 'Tied';
    return `${result} ${myScore} to ${opponentScore}`;
  }
  if (g.status === 'initiated') return 'Waiting for first move';
  return 'In progress';
}

function formatChallengeTime(createdAt) {
  if (!createdAt?.toDate) return '';
  return createdAt.toDate().toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function OnlineGames() {
  const { currentUser, authLoaded } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState(null);
  const [players, setPlayers] = useState([]);
  const [linkedPlayerId, setLinkedPlayerId] = useState(null);
  const [showNewChallengeModal, setShowNewChallengeModal] = useState(false);

  const [incomingChallenges, setIncomingChallenges] = useState([]);
  const [sentChallenges, setSentChallenges] = useState([]);
  const [pendingLinkChallenges, setPendingLinkChallenges] = useState([]);
  const [challengeError, setChallengeError] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [copiedChallengeId, setCopiedChallengeId] = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    getDocs(collection(db, 'players')).then(snap => {
      setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    getDocs(query(collection(db, 'players'), where('uid', '==', currentUser.uid))).then(snap => {
      setLinkedPlayerId(snap.empty ? null : snap.docs[0].id);
    });

    Promise.all([
      getDocs(query(collection(db, 'onlineGames'), where('player1Uid', '==', currentUser.uid))),
      getDocs(query(collection(db, 'onlineGames'), where('player2Uid', '==', currentUser.uid))),
    ]).then(([snap1, snap2]) => {
      const all = [
        ...snap1.docs.map(d => ({ id: d.id, ...d.data() })),
        ...snap2.docs.map(d => ({ id: d.id, ...d.data() })),
      ];
      setGames(all);
    });

    refreshChallenges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  function refreshChallenges() {
    getDocs(
      query(collection(db, 'challenges'), where('toUid', '==', currentUser.uid), where('status', '==', 'pending'))
    ).then(snap => {
      setIncomingChallenges(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    getDocs(
      query(collection(db, 'challenges'), where('fromUid', '==', currentUser.uid), where('status', '==', 'pending'))
    ).then(snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSentChallenges(all.filter(c => c.toPlayerId));
      setPendingLinkChallenges(all.filter(c => !c.toPlayerId));
    });
  }

  async function handleCopyChallengeLink(challengeId) {
    await navigator.clipboard.writeText(`${window.location.origin}/challenges/${challengeId}`);
    setCopiedChallengeId(challengeId);
  }

  async function handleAccept(challengeId) {
    setRespondingTo({ id: challengeId, action: 'accept' });
    setChallengeError(null);
    try {
      const acceptChallenge = httpsCallable(functions, 'acceptChallenge');
      const result = await acceptChallenge({ challengeId });
      navigate(`/online/${result.data.onlineGameId}`);
    } catch (err) {
      setChallengeError(err.message);
      setRespondingTo(null);
    }
  }

  async function handleDecline(challengeId) {
    setRespondingTo({ id: challengeId, action: 'decline' });
    setChallengeError(null);
    try {
      const declineChallenge = httpsCallable(functions, 'declineChallenge');
      await declineChallenge({ challengeId });
      refreshChallenges();
    } catch (err) {
      setChallengeError(err.message);
    } finally {
      setRespondingTo(null);
    }
  }

  async function handleCancel(challengeId) {
    setRespondingTo({ id: challengeId, action: 'cancel' });
    setChallengeError(null);
    try {
      const cancelChallenge = httpsCallable(functions, 'cancelChallenge');
      await cancelChallenge({ challengeId });
      refreshChallenges();
    } catch (err) {
      setChallengeError(err.message);
    } finally {
      setRespondingTo(null);
    }
  }

  if (authLoaded && !currentUser) return <p>Log in to see your games.</p>;
  if (games === null) return <p>Loading…</p>;

  const nameFor = id => players.find(p => p.id === id)?.name ?? 'Player';
  const otherPlayers = players.filter(p => p.id !== linkedPlayerId);

  function gameSortTime(g) {
    const ts = g.status === 'completed' ? g.finishedAt : g.createdAt;
    return ts?.toMillis?.() ?? 0;
  }

  const inProgressGames = games.filter(g => g.status !== 'completed').sort((a, b) => gameSortTime(b) - gameSortTime(a));
  const finishedGames = games.filter(g => g.status === 'completed').sort((a, b) => gameSortTime(b) - gameSortTime(a));

  const hasChallengeContent =
    incomingChallenges.length > 0 || sentChallenges.length > 0 || pendingLinkChallenges.length > 0;

  function renderGameRow(g) {
    const opponentId = g.player1Uid === currentUser.uid ? g.player2Id : g.player1Id;
    return (
      <div
        key={g.id}
        className="recent-game-row"
        onClick={() => navigate(`/online/${g.id}`)}
        style={{ cursor: 'pointer' }}
      >
        <span>vs {nameFor(opponentId)}</span>
        <span className="text-muted recent-game-date">
          {formatGameDate(g)} — {statusLabel(g, currentUser)}{g.status !== 'completed' && ` — Round ${g.round}`}
        </span>
      </div>
    );
  }

  return (
    <>
      <h2 className="section-title mb-3">Games</h2>

      {linkedPlayerId && (
        <div className="d-flex justify-content-end mb-3">
          <button type="button" className="pill-button" onClick={() => setShowNewChallengeModal(true)}>
            + New Game
          </button>
        </div>
      )}

      {challengeError && <Alert variant="danger">{challengeError}</Alert>}

      {linkedPlayerId && hasChallengeContent && (
        <div className="panel w-100 mb-3">
          {incomingChallenges.length > 0 && (
            <div className="mb-3">
              <h6>Received</h6>
              <Table striped bordered hover className="mb-0 table-compact">
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Sent</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {incomingChallenges.map(c => {
                    const fromPlayer = players.find(p => p.id === c.fromPlayerId);
                    return (
                      <tr key={c.id}>
                        <td>{fromPlayer?.name ?? 'Someone'}</td>
                        <td className="small">{formatChallengeTime(c.createdAt)}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="success"
                            className="me-2"
                            disabled={respondingTo?.id === c.id}
                            onClick={() => handleAccept(c.id)}
                          >
                            {respondingTo?.id === c.id && respondingTo.action === 'accept' ? 'Accepting…' : 'Accept'}
                          </Button>
                          <Button
                            size="sm"
                            variant="warning"
                            disabled={respondingTo?.id === c.id}
                            onClick={() => handleDecline(c.id)}
                          >
                            {respondingTo?.id === c.id && respondingTo.action === 'decline' ? 'Declining…' : 'Decline'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {sentChallenges.length > 0 && (
            <div className={incomingChallenges.length > 0 ? 'mb-3' : 'mb-0'}>
              <h6>Sent</h6>
              <Table striped bordered hover className="mb-0 table-compact">
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Sent</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sentChallenges.map(c => {
                    const toPlayer = players.find(p => p.id === c.toPlayerId);
                    return (
                      <tr key={c.id}>
                        <td>{toPlayer?.name ?? 'Player'}</td>
                        <td className="small">{formatChallengeTime(c.createdAt)}</td>
                        <td>Pending</td>
                        <td>
                          <Button
                            size="sm"
                            variant="warning"
                            disabled={respondingTo?.id === c.id}
                            onClick={() => handleCancel(c.id)}
                          >
                            {respondingTo?.id === c.id && respondingTo.action === 'cancel' ? 'Cancelling…' : 'Cancel'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {pendingLinkChallenges.length > 0 && (
            <div>
              <h6>Pending Challenge Links</h6>
              {pendingLinkChallenges.map(c => (
                <Form.Group key={c.id} className="mb-2">
                  <InputGroup>
                    <Form.Control
                      readOnly
                      value={`${window.location.origin}/challenges/${c.id}`}
                      onFocus={e => e.target.select()}
                    />
                    <Button onClick={() => handleCopyChallengeLink(c.id)}>
                      {copiedChallengeId === c.id ? 'Copied!' : 'Copy'}
                    </Button>
                    <Button
                      variant="warning"
                      disabled={respondingTo?.id === c.id}
                      onClick={() => handleCancel(c.id)}
                    >
                      {respondingTo?.id === c.id && respondingTo.action === 'cancel' ? 'Cancelling…' : 'Cancel'}
                    </Button>
                  </InputGroup>
                </Form.Group>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="panel w-100 mb-3">
        <div className="mb-3">
          <h2 className="section-title mb-0">In-progress</h2>
        </div>
        {inProgressGames.length === 0 && <p className="pb-3">No in-progress games</p>}
        {inProgressGames.map(renderGameRow)}
      </div>

      <div className="panel w-100 mb-3">
        <div className="mb-3">
          <h2 className="section-title mb-0">Finished</h2>
        </div>
        {finishedGames.length === 0 && <p className="pb-3">No finished games</p>}
        {finishedGames.map(renderGameRow)}
      </div>

      {linkedPlayerId && (
        <NewChallengeModal
          show={showNewChallengeModal}
          onHide={() => setShowNewChallengeModal(false)}
          players={otherPlayers}
          onChallengeCreated={refreshChallenges}
        />
      )}
    </>
  );
}
